import { usePermissao } from "@/contexts/autorizacao/permissao";
import { useEstabelecimento } from "@/contexts/cadastro/estabelecimento";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { ClearOutlined, FilterOutlined } from "@ant-design/icons";
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import { useSearchParams } from "react-router";

interface FormValues {
  estabelecimento: {
    id: number;
  };
  dataCadastro: Dayjs;
  numero: string;
  status: "EMITIDO" | "CANCELADO" | "RESOLVIDO" | "PENDENTE_PARECER";
}

export function FiltroTermoNotificacao() {
  const { isOnline } = useNetworkStatus();
  const [searchParams, setSearchParams] = useSearchParams();
  const { estabelecimentoOptions } = useEstabelecimento();
  const { hasEnabledPermissao } = usePermissao();
  const [form] = Form.useForm<FormValues>();
  const { message } = App.useApp();

  const handleSubmit = (values: FormValues) => {
    if (values.estabelecimento?.id) {
      searchParams.set("estabelecimentoId", String(values.estabelecimento.id));
    } else {
      searchParams.delete("estabelecimentoId");
    }

    if (values.dataCadastro) {
      searchParams.set(
        "dataCadastro",
        values.dataCadastro.format("YYYY-MM-DD")
      );
    } else {
      searchParams.delete("dataCadastro");
    }

    if (values.numero) {
      searchParams.set("numero", values.numero);
    } else {
      searchParams.delete("numero");
    }

    if (values.status) {
      searchParams.set("status", values.status);
    } else {
      searchParams.delete("status");
    }

    searchParams.set("page", "1");
    setSearchParams(searchParams);

    message.success({
      content: "Filtro aplicado com sucesso",
      key: "aplicar-filtro",
    });
  };

  const handleClear = () => {
    form.resetFields();
    searchParams.delete("estabelecimentoId");
    searchParams.delete("page");
    setSearchParams(searchParams);

    message.info({ content: "Filtro limpo", key: "limpar-filtro" });
  };

  useEffect(() => {
    const estabelecimentoId = searchParams.get("estabelecimentoId");
    const dataCadastro = searchParams.get("dataCadastro");
    const numero = searchParams.get("numero");
    const status = searchParams.get("status") as FormValues["status"];

    form.setFieldsValue({
      estabelecimento: {
        id: Number(estabelecimentoId) || undefined,
      },
      dataCadastro: dataCadastro ? dayjs(dataCadastro) : undefined,
      numero: numero || undefined,
      status: status || undefined,
    });
  }, []);

  return (
    <>
      <Card
        styles={{
          body: {
            backgroundColor: "#f0f2f5",
            borderRadius: 0,
          },
          actions: {
            backgroundColor: "#fafafa",
            borderRadius: 0,
          },
        }}
        actions={[
          <Space key="actions">
            <Button
              icon={<ClearOutlined />}
              onClick={handleClear}
              htmlType="button"
            >
              Limpar
            </Button>

            <Button
              form="form-filtro"
              type="primary"
              htmlType="submit"
              icon={<FilterOutlined />}
              disabled={
                !isOnline ||
                !hasEnabledPermissao("TERMO_NOTIFICACAO_VISUALIZAR")
              }
            >
              Aplicar
            </Button>
          </Space>,
        ]}
        style={{ boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}
      >
        <Card.Meta title="Filtro" />

        <Divider />

        <Form
          id="form-filtro"
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
              <Form.Item
                name={["estabelecimento", "id"]}
                rules={[
                  { required: true, message: "Selecione um estabelecimento" },
                ]}
              >
                <Select
                  placeholder="Estabelecimento"
                  showSearch
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  optionFilterProp="label"
                  options={estabelecimentoOptions}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
              <Form.Item name="status">
                <Select placeholder="Status" allowClear>
                  <Select.Option value="EMITIDO">Emitido</Select.Option>
                  <Select.Option value="CANCELADO">Cancelado</Select.Option>
                  <Select.Option value="RESOLVIDO">Resolvido</Select.Option>
                  <Select.Option value="PENDENTE_PARECER">
                    Pendente de Parecer
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
              <Form.Item name="dataCadastro">
                <DatePicker
                  placeholder="Data de cadastro"
                  format={"DD/MM/YYYY"}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
              <Form.Item name="numero">
                <Input placeholder="N° Termo" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </>
  );
}
